import type { ListingDetails } from 'app/types/listingDetails.type';
import type { SimilarListingDetails } from 'app/types/similarListingDetails.type';
import type { HotpadsListing } from 'app/client/analytics/types';
import {
  type ContactFormLeadTriggerObject,
  type ContactFormTriggerLocation,
  emitTrackDemotedListingLeadEvent,
  emitTrackFreeListingLeadEvent,
  emitTrackPaidListingLeadEvent,
  emitTrackPhoneLeadForDemotedPropertyEvent,
  emitTrackPhoneLeadForFreePropertyEvent,
  emitTrackPhoneLeadForPaidPropertyEvent,
  type LeadTriggerSource,
  type PhoneLeadTriggerObject,
} from '../models/Clickstream/ContactClickstreamEvents';

type ListingType = 'free' | 'paid' | 'paidDemoted';
type LeadType = 'submit' | 'phone';

// Type definitions for the tracking functions
type SubmitTrackingFunction = (params: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: ContactFormLeadTriggerObject;
  triggerSource: LeadTriggerSource;
}) => void;

type PhoneTrackingFunction = (params: {
  listing: HotpadsListing | null | undefined;
  triggerLocation?: ContactFormTriggerLocation;
  triggerObject: PhoneLeadTriggerObject;
}) => void;

export const getSimilarListingType = (similarListing: SimilarListingDetails): ListingType => {
  const { isDemoted, isPaidAndNotDemoted } = similarListing;

  return isDemoted ? 'paidDemoted' : isPaidAndNotDemoted ? 'paid' : 'free';
};

export const getListingType = (listing: ListingDetails): ListingType => {
  let listingType: ListingType = 'free';
  const { details, isPaidAndNotDemoted } = listing;
  const demoted = details?.demoted;

  if (demoted) {
    listingType = 'paidDemoted';
  } else if (isPaidAndNotDemoted) {
    listingType = 'paid';
  }

  return listingType;
};

const mapTrackingFunctions: {
  submit: Record<ListingType, SubmitTrackingFunction>;
  phone: Record<ListingType, PhoneTrackingFunction>;
} = {
  submit: {
    free: emitTrackFreeListingLeadEvent,
    paid: emitTrackPaidListingLeadEvent,
    paidDemoted: emitTrackDemotedListingLeadEvent,
  },
  phone: {
    free: emitTrackPhoneLeadForFreePropertyEvent,
    paid: emitTrackPhoneLeadForPaidPropertyEvent,
    paidDemoted: emitTrackPhoneLeadForDemotedPropertyEvent,
  },
};

// Overloaded function signatures for proper type inference based on leadType
export function getTrackingFunction(params: {
  type: ListingType;
  leadType: 'submit';
}): SubmitTrackingFunction | undefined;
export function getTrackingFunction(params: {
  type: ListingType;
  leadType: 'phone';
}): PhoneTrackingFunction | undefined;
export function getTrackingFunction(params: {
  type: ListingType;
  leadType?: undefined;
}): SubmitTrackingFunction | undefined;
export function getTrackingFunction({
  type,
  leadType = 'submit',
}: {
  type: ListingType;
  leadType?: LeadType;
}): SubmitTrackingFunction | PhoneTrackingFunction | undefined {
  return mapTrackingFunctions?.[leadType]?.[type];
}
